import { useState } from "react";
import { useMutation } from "convex/react";
import {
  Button,
  ButtonGroup,
  CloseButton,
  Dialog,
  Field,
  Input,
  NativeSelect,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@/lib/convex";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { SinglePersonPicker } from "../components/SinglePersonPicker";
import { toastError, toastSuccess } from "../components/ui/toaster";
import {
  buildActor,
  buildReviewers,
  countReviewers,
  type ReviewMode,
  type TaskPeople,
} from "./ActivityAdminShared";
import { applyCompletionValue, completionValueOf, CompletionSelect, ReviewerPicker } from "./ActivityEditorParts";
import type { AssignmentTask } from "./ActivityDetailParts";

/** One copy's step, as the editor's controls hold it. */
function peopleOf(task: AssignmentTask): TaskPeople {
  const actor = task.actor;
  return {
    actorKind: actor.kind,
    actorRole: actor.kind === "role" ? actor.role : "activityAdmin",
    actorPersonId: actor.kind === "person" ? String(actor.personId) : null,
    satisfiedByParticipation: false,
    participationRoles: [],
    reviewerSupervisor: task.reviewers.some((spec) => spec.kind === "supervisor"),
    reviewerCoSupervisor: task.reviewers.some((spec) => spec.kind === "coSupervisor"),
    reviewerNamedAtAssignment: false,
    reviewerRoles: task.reviewers.flatMap((spec) => spec.kind === "role" ? [spec.role] : []),
    reviewerPersonIds: task.reviewers.flatMap((spec) => spec.kind === "person" ? [String(spec.personId)] : []),
    reviewMode: task.reviewMode,
  };
}

/**
 * Change who does one step of one person's copy, or who signs it off.
 *
 * The same controls as the activity editor, without the catalogue-only
 * answers. For activity admins; the backend refuses everybody else.
 */
export function TaskPeopleDialog({
  task,
  open,
  onClose,
}: {
  task: AssignmentTask;
  open: boolean;
  onClose: () => void;
}) {
  const setTaskPeople = useMutation(api.personActivities.adminSetTaskPeople);
  const directory = useQuery(api.persons.listCeMMies, open ? {} : "skip");
  const people = directory?.persons ?? [];

  const [draft, setDraft] = useState<TaskPeople>(() => peopleOf(task));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // A step completed by a record is always the assignee's; only its
  // reviewers can change.
  const actorFixed = task.participationCriteria !== undefined;

  async function save() {
    const actor = buildActor(draft);
    if (actor.kind === "namedAtAssignment") {
      return;
    }
    const reviewers = buildReviewers(draft).flatMap((spec) => spec.kind === "namedAtAssignment" ? [] : [spec]);
    setSaving(true);
    try {
      await setTaskPeople({
        taskId: task._id as Id<"personActivityTasks">,
        actor,
        reviewers,
        reviewMode: reviewers.length === 0 ? null : (draft.reviewMode ?? "any"),
        reason,
      });
      toastSuccess("Step updated.");
      onClose();
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Could not change the step.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(event) => {
        if (!event.open) {
          onClose();
        }
      }}
      // Fresh from the step each time it opens, so a cancelled edit is gone.
      onExitComplete={() => {
        setDraft(peopleOf(task));
        setReason("");
      }}
      size="lg"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{task.titleSnapshot}</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap={5}>
                <Text fontSize="sm" color="fg.muted">
                  For this person&rsquo;s copy only. The activity in the catalogue, and everybody
                  else&rsquo;s copies, stay as they are.
                </Text>

                <Field.Root>
                  <Field.Label>Who does it</Field.Label>
                  {actorFixed ? (
                    <Text fontSize="sm" color="fg.muted">The assignee: a record settles this step.</Text>
                  ) : (
                    <CompletionSelect
                      forCopy
                      value={completionValueOf(draft)}
                      onValueChange={(value) => setDraft((current) => applyCompletionValue(current, value))}
                    />
                  )}
                </Field.Root>
                {!actorFixed && draft.actorKind === "person" ? (
                  <SinglePersonPicker
                    label="Which person"
                    people={people}
                    selectedPersonId={draft.actorPersonId}
                    onChange={(actorPersonId) => setDraft((current) => ({ ...current, actorPersonId }))}
                  />
                ) : null}

                <Field.Root>
                  <Field.Label>Who signs it off</Field.Label>
                  <ReviewerPicker
                    forCopy
                    people={people}
                    task={draft}
                    onChange={(picked) => setDraft((current) => {
                      const next = {
                        ...current,
                        reviewerSupervisor: picked.supervisor,
                        reviewerCoSupervisor: picked.coSupervisor,
                        reviewerRoles: picked.roles,
                        reviewerPersonIds: picked.personIds,
                      };
                      return { ...next, reviewMode: countReviewers(next) === 0 ? null : (current.reviewMode ?? "any") };
                    })}
                  />
                </Field.Root>
                {countReviewers(draft) > 1 ? (
                  <Field.Root>
                    <Field.Label>How many have to approve</Field.Label>
                    <NativeSelect.Root size="sm" maxW="sm">
                      <NativeSelect.Field
                        value={draft.reviewMode ?? "any"}
                        onChange={(event) => setDraft((current) => ({ ...current, reviewMode: event.target.value as ReviewMode }))}
                      >
                        <option value="any">Any one of them</option>
                        <option value="all">Every one of them</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                ) : null}

                <Field.Root required>
                  <Field.Label>
                    Why
                    <Field.RequiredIndicator />
                  </Field.Label>
                  <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Recorded in the history" />
                  <Field.HelperText>Whoever is newly asked to do or sign this step is told, with this reason.</Field.HelperText>
                </Field.Root>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <ButtonGroup gap={2}>
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                <Button
                  colorPalette="blue"
                  loading={saving}
                  disabled={reason.trim() === "" || (draft.actorKind === "person" && draft.actorPersonId === null)}
                  onClick={() => void save()}
                >
                  Save
                </Button>
              </ButtonGroup>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
